import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Prompt } from 'react-router';
import PropTypes from 'prop-types';
import path from 'path';
import { daoDelete, daoPost, daoPut } from 'mrf/Dao';
import { resourceGetObject } from 'mrf/redux/ResourceFetcher';
import { Spinner } from 'styles/components/Spinner';
import PageTitle from './Title';
import Button from './Button';
import { Grid, Col } from './Grid';
import { Unflatten } from '../redux/Mapper';
import { useDispatch, useSelector } from 'react-redux';
import Paper from 'mrf/view/Paper';
import Separator from 'styles/basic/Separator';
import { deleteResourceRaw } from 'mrf/redux/actions/resource';
import IF from 'mrf/view/IF';
import mrfConfig from 'mrf.config';
import notification, { TYPE } from 'mrf/utils/notification';
import { errorValidationHandler } from 'mrf/utils/ValidationUtils';
import { setFormMetaValueByContextAction } from 'mrf/redux/actions/form';
import useMRFTranslation from 'mrf/utils/useMRFTranslation';
import mrf from "../../mrf.config";
import DeleteModal from 'mrf/view/DeleteModal';
import IfAuth from 'mrf/utils/IfAuth';
import ConfirmationModal from 'mrf/view/ConfirmationModal';
import T from "mrf/utils/T";
export const FormContext = React.createContext();

export const mrfErrorValidationHandler = mrfConfig.overrideValidationFunction ?? errorValidationHandler

export const SubFormContext = ({ keys, children }) => {
    const context = useContext(FormContext);

    const subContext = useMemo(
        () => ({ ...context, keys: [ ...context.keys, ...keys ] }),
        [context, keys],
    )

    return <FormContext.Provider value={subContext} children={children} />;
}

export const formPromptMessage = 'Changes you made may not be saved.';

export const FormPrompt = ({ condition, message }) => {
    const t = useMRFTranslation();

    return <Prompt when={condition} message={message ?? t(formPromptMessage)} />
}

export const FormWrapper = ({ context, promptCondition = false, message, children }) => {
    useEffect(() => {
        if (promptCondition) {
            window.onbeforeunload = () => true;
        }

        return () => {
            window.onbeforeunload = undefined;
        }
    }, [promptCondition]);

    return <>
        <FormPrompt condition={promptCondition} message={message} />
        <FormContext.Provider value={context} children={children} />
    </>
}

FormCRUD.propTypes = {
    match: PropTypes.object,
    history: PropTypes.object,

    context: PropTypes.object,
    descriptor: PropTypes.object,
    title: PropTypes.any.isRequired, // Form title.
    id: PropTypes.any, // Determines if is Edit mode, id of the object being edited.
    map: PropTypes.func, // Mapping function to transform the object before submitting.
    validateForm: PropTypes.func, // Validate form before submitting.
    handleError: PropTypes.func, // Handle any errors returned by the endpoint.
    children: PropTypes.any,
    viewAfterPost: PropTypes.bool, // Whether or not to redirect to a View page after a successful submit
    viewAfterPostPath: PropTypes.string, // Redirect path after a successful submit. The ID will be automatically appended
    overrideAfterPath: PropTypes.string, // Override the path to go after the form is submitted.
    disableAutoValidate: PropTypes.bool, // Whether or not to autovalidate fields in back-end during edit
    handleCancel: PropTypes.func,
    additionalInfoModalDelete: PropTypes.string, // additional notes displayed in the delete modal
    roleForDelete: PropTypes.arrayOf(PropTypes.string), // array of role codes to control access to the delete button
    cancelLabel: PropTypes.string,  // text displayed on the cancel button (default value is 'Cancel').
    saveLabel: PropTypes.string,  // text displayed on the save button (default value is 'Save').
    hideDelete: PropTypes.bool,  // flag that determines whether the delete button should be hidden.
    hideButtons: PropTypes.bool,  // flag that determines whether action buttons.
    actions: PropTypes.any,  // additional or custom actions to be displayed in the form.
    titlePrefix: PropTypes.string,  // optional prefix to be added to the form title.
    additionalInfoModal: PropTypes.string, // additional notes displayed in the confirmation modal
    showConfirmationModal: PropTypes.bool, // flag that determines whether the confirmation modal should be displayed.
    actionAfterSave: PropTypes.func, // function to be called after successful saved
    skipGetAfterEdit: PropTypes.bool // flag to skip fetching the object again after editing
};

export default function FormCRUD({
    children,
    match,
    history,
    title,
    context,
    descriptor,
    id = match.params.id,
    map = o => o,
    validateForm = () => true,
    handleError,
    cancelLabel = 'Cancel',
    saveLabel = 'Save',
    viewAfterPost,
    viewAfterPostPath = '../view/',
    overrideAfterPath,
    disableAutoValidate = false,
    endpoint,
    hideDelete=false,
    hideButtons=false,
    actions,
    titlePrefix,
    handleCancel,
    additionalInfoModalDelete,
    roleForDelete,
    additionalInfoModal, 
    showConfirmationModal,
    actionAfterSave,
    skipGetAfterEdit
}) {
    const readOnly = context?.readOnly;

    const [bufferedTimeout, setBufferedTimeout] = useState();

    const form = useSelector(state => state?.forms?.[context?.resource]);

    const dispatch = useDispatch();

    const submitLock = useRef(false);

    endpoint = endpoint ?? `/api/${context?.resource}`;
    handleCancel = handleCancel ?? history.goBack;
   
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    // open the delete modal
    const handleDelete = () => {
        setIsModalOpen(true); 
    };

    // open the confirmation modal
    const handleConfirmModal = (ev) => {
        if (showConfirmationModal) {
            setIsConfirmModalOpen(true);
        } else {
            handleSubmit(ev);
        }
    };

    const submit = async action => {
        try {
            let objectId;

            const data = { ...form };
            delete data._meta;

            if (id) {
                await daoPut(`${endpoint}/${id}`, map(Unflatten(data, descriptor)), action);
                objectId = id;
            } else {
                const savedObject = await daoPost(endpoint, map(Unflatten(data, descriptor)), action);
                objectId = savedObject?.id;
            }

            actionAfterSave(data);

            return objectId;
        } catch (e) {
            mrfErrorValidationHandler(e, context, action);
            throw e;
        }
    }

    const handleSubmit = async e => {
        e.preventDefault();
        if (submitLock.current) {
            return;
        }

        if (!validateForm(form)) {
            return;
        }

        try {
            submitLock.current = true;
            clearTimeout(bufferedTimeout);
            const objectId = await submit('execute');

            dispatch(setFormMetaValueByContextAction(context, { dirty: false }));
            dispatch(deleteResourceRaw(context?.resource));

            // 'id' is undefined when posting a new object
            if (id) {

                if (!skipGetAfterEdit) {
                    resourceGetObject(dispatch, context?.resource, `${endpoint}/${id}`, undefined, descriptor);
                }
                notification({ type: TYPE.SUCCESS, message: 'Successfully Edited' });
            } else {
                dispatch(deleteResourceRaw(context?.resource));
                notification({ type: TYPE.SUCCESS, message: 'Successfully Added' });
            }

            if (overrideAfterPath) {
                history.push(path.join(match.url, overrideAfterPath));
            } else if (!id && viewAfterPost) { // clients can choose to use a custom redirect path on posts
                history.push(path.join(match.url, viewAfterPostPath, `/${objectId}`));
            } else {
                history.push(path.join(match.url, id ? '../..' : '..'));
            }
        } catch (e2) {
            console.error(e2);
            handleError?.(e2);
        } finally {
            submitLock.current = false;
        }
    }

    const handleConfirmDelete = async () => {
        setIsModalOpen(false);

        try {
            mrf.usePostForDelete ? await daoPost(`${endpoint}/${id}/delete`, '') 
                : await daoDelete(`${endpoint}/${id}`);
            dispatch(setFormMetaValueByContextAction(context, { dirty: false }));
            dispatch(deleteResourceRaw(context?.resource));
            notification({ type: TYPE.SUCCESS, message: 'Successfully Deleted' });
            history.push(path.join(match.url, '../..'));
        } catch (e) {
            if (!e?.title) {
                notification({ type: TYPE.ERROR, message: 'An unexpected error has occurred!' });
            }
            console.error(e);
        }
    };

    //This is an auto-validator, we don't care about the error being caught.
    const validate = async () => {
        try {
            if (mrfConfig.validateForm) {
                await submit('validate');
            }
        } catch (e) {
            mrfErrorValidationHandler(e, context);
        }
    };

    useEffect(() => {
        if (disableAutoValidate) {
            return;
        }
        const timeout = setTimeout(validate, 1000);
        setBufferedTimeout(timeout);
        return () => clearTimeout(timeout);
    }, [form]); // eslint-disable-line

    if (!form) {
        return <Spinner />;
    }

    // list of roles that have permission to delete
    const deleteButtonRoleCodes = roleForDelete && roleForDelete.length > 0 
        ? roleForDelete.map(role => String(role).toUpperCase()) 
        : [];

    return <>
        <FormWrapper context={context} promptCondition={form?._meta?.dirty}>
            <div className='w-full'>
                <form onSubmit={handleSubmit}>
                    <Paper>
                        <PageTitle>{titlePrefix ?? (!readOnly && (id ? 'Edit' : 'New'))} <T>{title}</T></PageTitle>

                        {children}

                        <IF condition={!readOnly && !hideButtons}>
                            <Separator />
                            <Grid>
                                <IF condition={!actions}>
                                    <Col size={3}>
                                        <IF condition={id && !hideDelete}>
                                            <IfAuth roleCodes={deleteButtonRoleCodes}>
                                                <Button color='delete' type='button' onClick={handleDelete}>Delete</Button>
                                            </IfAuth>
                                        </IF>
                                    </Col>
                                    <Col size={3} />
                                </IF>
                                <IF condition={!!actions}>
                                    {actions}
                                </IF>
                                <Col size={3}>
                                    <Button color='secondary' type='button' onClick={handleCancel}>{cancelLabel}</Button>
                                </Col>
                                <Col size={3}>
                                    <Button color='primary' type='submit' onClick={handleConfirmModal}>{saveLabel}</Button>
                                </Col>
                            </Grid>
                        </IF>
                    </Paper>
                </form>
            </div>
        </FormWrapper>

        <DeleteModal 
            isOpen={isModalOpen} 
            onCancel={() => setIsModalOpen(false)} 
            onConfirm={handleConfirmDelete} 
            title={title} 
            name={form?.name} 
            additionalInfoModalDelete={additionalInfoModalDelete}
        />

        {showConfirmationModal && (
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onCancel={() => setIsConfirmModalOpen(false)}
                onConfirm={handleSubmit}
                additionalInfoModal={additionalInfoModal}
            />
        )}
    </>
}

FormCRUD.defaultProps = {
    cancelLabel: 'Cancel',
    saveLabel: 'Save',
    viewAfterPost: false,
    viewAfterPostPath: '../view/',
    overrideAfterPath: '',
    disableAutoValidate: false,
    hideDelete: false,
    hideButtons: false,
    actions: null,
    titlePrefix: '',
    handleCancel: null,
    roleForDelete: [],
    showConfirmationModal: false,
    actionAfterSave: () => {},
    skipGetAfterEdit: false,
};